//  IMPORTANT: updates to this registry must also be added to the js_modules list in theme.liquid, otherwise you
//  will not receive code updates in staging beyond the initial deploy of new component code.
import Header from '@theme/header.js';
import Cart from '@theme/cart.js';
import FooterNewsletter from '@theme/footer-newsletter.js';
import PdpHero from '@theme/pdp-hero.js';
import PdpStickyCta from '@theme/pdp-sticky-cta.js';
import PdpSelection from '@theme/pdp-selection.js';
import TestimonialsSlider from '@theme/testimonials-slider.js';
import Press from '@theme/press.js';
import HomepageHeroCarousel from '@theme/homepage-hero-carousel.js';
import Hero from '@theme/hero.js';
import FeaturesTabs from '@theme/features-tabs.js';
import LifeWithLuffuTimeline from '@theme/life-with-luffu-timeline.js';
import LifeWithLufuScroll from '@theme/life-with-lufu-scroll.js';
import Timeline from '@theme/timeline.js';
import InsightCarousel from '@theme/insight-carousel.js';
import LinkColorPicker from '@theme/link-color-picker.js';
import LinkFeatures from '@theme/link-features.js';
import Faq from '@theme/faq.js';
import VideoCover from '@theme/video-cover.js';
import { refreshProductContext } from '@theme/product-context.js';

/**
 * Component registry keyed by the value of the `data-component` attribute.
 * 
 * @type {Record<string, typeof import('./base-component.js').default>}
 */
const components = {
    'header': Header,
    'cart': Cart,
    'footer-newsletter': FooterNewsletter,
    'pdp-hero': PdpHero,
    'pdp-sticky-cta': PdpStickyCta,
    'pdp-selection': PdpSelection,
    'testimonials-slider': TestimonialsSlider,
    'press': Press,
    'homepage-hero-carousel': HomepageHeroCarousel,
    'hero': Hero,
    'features-tabs': FeaturesTabs,
    'life-with-luffu-timeline': LifeWithLuffuTimeline,
    'life-with-lufu-scroll': LifeWithLufuScroll,
    'timeline': Timeline,
    'insight-carousel': InsightCarousel,
    'link-color-picker': LinkColorPicker,
    'link-features': LinkFeatures,
    'faq': Faq,
}

/**
 * Live instances keyed by Shopify section id so they can be destroyed on unload.
 * @type {Record<string, InstanceType<typeof import('./base-component.js').default>>}
 */
const instances = {};

/**
 * Instantiate video-cover controllers within a root
 * @param {ParentNode} [root] - Subtree to scan (defaults to document)
 */
function createVideoCovers (root = document) {
  root.querySelectorAll('[data-video-cover]').forEach(el => {
    // ??= keeps the existing instance if already instantiated
    el.videoCover ??= new VideoCover(el);
  });
}

/**
 * Destroy video-cover controllers within a root and drop their references
 * @param {ParentNode} root
 */
function destroyVideoCovers (root) {
  root.querySelectorAll('[data-video-cover]').forEach(el => {
    el.videoCover?.destroy();
    delete el.videoCover;
  });
}

document.querySelectorAll('[data-component]').forEach(el => {
  const { component, sectionId } = el.dataset;
  if (components[component]) {
    instances[sectionId] = new components[component](el);
  }
});
createVideoCovers();

/**
 * Theme editor: a section was added or re-rendered — instantiate its component.
 * @param {CustomEvent & { target: HTMLElement }} e
 */
/**
 * Re-init the timeline when a scene section is edited in the theme editor.
 */
function refreshTimeline () {
  const el = document.querySelector('[data-component="life-with-luffu-timeline"]');
  if (!el) return;
  const { sectionId } = el.dataset;
  instances[sectionId]?.destroy();
  instances[sectionId] = new LifeWithLuffuTimeline(el);
}

document.addEventListener('shopify:section:load', e => {
    // refresh product context for sticky cta on product pages
    // short circuits elsewhere
    refreshProductContext();
    createVideoCovers(e.target);
    const el = e.target.querySelector('[data-component]');
    if (el) {
      const { component, sectionId } = el.dataset;
      if (components[component]) {
          instances[sectionId] = new components[component](el);
      }
    }
    if (e.target.querySelector('[data-timeline-scene]')) {
      refreshTimeline();
    }
});

/**
 * Theme editor: a section was removed — tear down its instance to drop listeners.
 * @param {CustomEvent & { target: HTMLElement }} e
 */
document.addEventListener('shopify:section:unload', e => {
  destroyVideoCovers(e.target);
  const el = e.target.querySelector('[data-component]');
  if (el) {
    const { sectionId } = el.dataset;
    if (instances[sectionId]) {
      instances[sectionId].destroy();
      delete instances[sectionId];
    }
  }
  if (e.target.querySelector('[data-timeline-scene]')) {
    refreshTimeline();
  }
  refreshProductContext();
});
